import React, { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { useShop } from '../../contexts/ShopContext'
import { useToast } from '../../contexts/ToastContext'

const BUCKET = process.env.REACT_APP_SUPABASE_CUSTOM_PROCESSORS_BUCKET || 'custom-processors'

type StorageFile = {
  name: string
  id?: string
  updated_at?: string
  created_at?: string
  last_accessed_at?: string
  metadata?: { size?: number; mimetype?: string } | null
}

interface CustomProcessorsTabProps {
  canUpload: boolean
}

// Available processor types
const PROCESSOR_TYPES = [
  { key: 'Title', label: 'Title Processing', description: 'Product title generation and modification' },
  { key: 'Description', label: 'Description Processing', description: 'Product description generation' },
  { key: 'Tags', label: 'Tags Processing', description: 'Product tagging and categorization' },
  { key: 'Categories', label: 'Categories Processing', description: 'Product category assignment' },
  { key: 'Pricing', label: 'Pricing Processing', description: 'Price calculation and tiers' },
  { key: 'Translations', label: 'Translations Processing', description: 'Size name and value translations' },
  { key: 'ProductType', label: 'Product Type Processing', description: 'Product type assignment and SKU inclusion' },
  { key: 'Metafields', label: 'Metafields Processing', description: 'Custom metafield assignment' },
] as const

const CustomProcessorsTab: React.FC<CustomProcessorsTabProps> = ({ canUpload }) => {
  const { user } = useAuth()
  const { currentShopId } = useShop()
  const toast = useToast()
  const [files, setFiles] = useState<StorageFile[]>([])
  const [loadingList, setLoadingList] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [previewName, setPreviewName] = useState<string>('')
  const [previewText, setPreviewText] = useState<string>('')
  const [bucketOk, setBucketOk] = useState<boolean>(true)
  const [processorOverrides, setProcessorOverrides] = useState<string[]>([])
  const [loadingOverrides, setLoadingOverrides] = useState(false)
  const [savingOverrides, setSavingOverrides] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const prefix = user?.id ? `${user.id}/` : ''

  const resetPreview = () => {
    setPreviewName('')
    setPreviewText('')
  }

  const loadProcessorOverrides = useCallback(async () => {
    if (!user || !currentShopId) return
    setLoadingOverrides(true)
    try {
      const { data, error } = await supabase
        .from('merchant_config')
        .select('config_object')
        .eq('merchant_id', currentShopId)
        .single()

      if (error) throw error

      const configObject = data?.config_object as any
      const overrides = configObject?.processor_overrides?.keep_database_processors || []
      setProcessorOverrides(overrides)
    } catch (err: any) {
      console.error('Error loading processor overrides:', err)
      toast.error('Failed to load processor overrides')
    } finally {
      setLoadingOverrides(false)
    }
  }, [user, currentShopId, toast])

  const saveProcessorOverrides = async () => {
    if (!user || !currentShopId) return
    setSavingOverrides(true)
    try {
      // First get current config
      const { data: currentData, error: fetchError } = await supabase
        .from('merchant_config')
        .select('config_object')
        .eq('merchant_id', currentShopId)
        .single()

      if (fetchError) throw fetchError

      // Update the config object
      const updatedConfig = {
        ...(currentData?.config_object || {}),
        processor_overrides: {
          keep_database_processors: processorOverrides
        }
      }

      const { error: updateError } = await supabase
        .from('merchant_config')
        .update({
          config_object: updatedConfig,
          updated_at: new Date().toISOString()
        })
        .eq('merchant_id', currentShopId)

      if (updateError) throw updateError

      toast.success('Processor overrides saved successfully!')
    } catch (err: any) {
      console.error('Error saving processor overrides:', err)
      toast.error('Failed to save processor overrides')
    } finally {
      setSavingOverrides(false)
    }
  }

  const toggleProcessorOverride = (processorKey: string) => {
    setProcessorOverrides(prev =>
      prev.includes(processorKey)
        ? prev.filter(key => key !== processorKey)
        : [...prev, processorKey]
    )
  }

  const listFiles = useCallback(async () => {
    if (!user) return
    setLoadingList(true)
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .list(prefix, { limit: 100, offset: 0, sortBy: { column: 'updated_at', order: 'desc' } as any })

      if (error) {
        if ((error as any)?.message?.toLowerCase?.().includes('not found')) {
          setBucketOk(false)
        }
        throw error
      }
      setBucketOk(true)
      setFiles(data || [])
    } catch (err: any) {
      console.error('Error listing files:', err)
      toast.error(err.message || 'Failed to list files')
    } finally {
      setLoadingList(false)
    }
  }, [user, prefix, toast])

  useEffect(() => {
    listFiles()
    loadProcessorOverrides()
  }, [listFiles, loadProcessorOverrides])

  const handleSelectClick = () => {
    inputRef.current?.click()
  }

  const onlyTs = (fl: File[]) => fl.filter((f) => f.name.toLowerCase().endsWith('.ts'))

  const onFilesChosen = async (fileList: FileList | null) => {
    if (!user || !fileList || fileList.length === 0) return
    const chosen = Array.from(fileList)
    const tsFiles = onlyTs(chosen)
    if (tsFiles.length === 0) {
      toast.error('Please select at least one .ts file')
      return
    }
    await uploadFiles(tsFiles)
    // reset input value so same file can be reselected if needed
    if (inputRef.current) inputRef.current.value = ''
  }

  const uploadFiles = async (tsFiles: File[]) => {
    if (!user) return
    setUploading(true)
    toast.info(`Uploading ${tsFiles.length} file(s)...`)
    try {
      for (const file of tsFiles) {
        const key = `${prefix}${Date.now()}_${file.name}`
        const { error } = await supabase.storage
          .from(BUCKET)
          .upload(key, file, {
            cacheControl: '3600',
            upsert: true,
            contentType: 'text/plain',
          })
        if (error) throw error
      }
      toast.success('Upload complete')
      await listFiles()
    } catch (err: any) {
      console.error('Upload error:', err)
      toast.error(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (!e.dataTransfer?.files) return
    await onFilesChosen(e.dataTransfer.files)
  }

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }

  const previewFile = async (name: string) => {
    if (!user) return
    setPreviewName(name)
    setPreviewText('Loading...')
    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .download(`${prefix}${name}`)
      if (error) throw error
      const text = await data.text()
      setPreviewText(text)
    } catch (err: any) {
      console.error('Preview error:', err)
      setPreviewText('Failed to load file')
    }
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white  mb-2">CUSTOM PROCESSORS</h2>
      <p className="text-sm text-white/50  -mt-3 mb-4">Upload TypeScript (.ts) files and preview them from Supabase Storage.</p>

      {!bucketOk && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 p-3  text-sm">
          Supabase storage bucket "{BUCKET}" was not found or is not accessible. Create it in your Supabase project and allow authenticated users to read/write to the folder "{prefix || '{user.id}/'}".
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upload panel */}
        <div className="bg-transparent border border-white/[0.08] p-6">
          <h3 className="text-base font-bold text-orange-400  mb-4">UPLOAD .TS FILES</h3>

          {canUpload ? (
            <>
              <div
                className={`border-2 border-dashed ${uploading ? 'border-white/15' : 'border-orange-500/30'}  p-6 text-center cursor-pointer`}
                onDrop={onDrop}
                onDragOver={onDragOver}
                onClick={handleSelectClick}
              >
                <p className="text-sm text-white/70 ">Drag and drop .ts files here, or click to select</p>
                <p className="text-xs text-white/35  mt-1">Files will be stored under {BUCKET}/{prefix || '{user.id}/'}</p>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".ts"
                  multiple
                  className="hidden"
                  onChange={(e) => onFilesChosen(e.target.files)}
                />
              </div>

              <div className="mt-4 flex items-center gap-3">
                <button
                  onClick={listFiles}
                  disabled={loadingList}
                  className="px-4 py-2 bg-orange-500/10 text-orange-400 border border-orange-500/20 hover:bg-orange-500/15 transition-colors "
                >
                  {loadingList ? 'REFRESHING...' : 'REFRESH LIST'}
                </button>
                {uploading && <span className="text-xs text-white/35 ">Uploading...</span>}
              </div>
            </>
          ) : (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-yellow-700 p-4 text-sm ">
              Uploading custom processors is not included in your plan. Contact us to enable it.
            </div>
          )}
        </div>

        {/* Files panel */}
        <div className="bg-transparent border border-white/[0.08] p-6">
          <h3 className="text-base font-bold text-orange-400  mb-4">UPLOADED FILES</h3>

          {files.length === 0 ? (
            <div className="text-center py-10 text-white/25  text-sm">No files uploaded yet</div>
          ) : (
            <ul className="divide-y divide-white/[0.06]">
              {files.map((f) => (
                <li key={f.name} className="py-2 flex items-center justify-between">
                  <button
                    className="text-left text-orange-500 hover:underline  text-sm truncate"
                    onClick={() => previewFile(f.name)}
                    title={f.name}
                  >
                    {f.name}
                  </button>
                  <span className="text-xs text-white/35  ml-4">
                    {f.updated_at ? new Date(f.updated_at).toLocaleString() : ''}
                    {f.metadata?.size ? ` • ${(f.metadata.size / 1024).toFixed(1)} KB` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Preview panel */}
      <div className="bg-transparent border border-white/[0.08] p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-bold text-orange-400 ">PREVIEW</h3>
          {previewName && (
            <button className="text-xs text-white/50 hover:text-white " onClick={resetPreview}>CLEAR</button>
          )}
        </div>
        {previewName ? (
          <div>
            <div className="mb-2 text-sm text-white/70 ">{previewName}</div>
            <pre className=" border border-white/15 p-4 overflow-auto text-xs text-white ">
{previewText}
            </pre>
          </div>
        ) : (
          <div className="text-sm text-white/35 ">Select a file to preview its contents</div>
        )}
      </div>

      {/* Processor Override Settings */}
      <div className="bg-transparent border border-white/[0.08] p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-orange-400 ">PROCESSOR OVERRIDE SETTINGS</h3>
          <button
            onClick={saveProcessorOverrides}
            disabled={savingOverrides || loadingOverrides}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 px-4 rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
          >
            {savingOverrides ? 'SAVING...' : 'SAVE OVERRIDES'}
          </button>
        </div>

        <div className="bg-orange-500/10 border border-orange-500/20 p-4 mb-6">
          <p className="text-sm  text-white/70 mb-2">
            Control which processors are used when both database and custom processors are available.
          </p>
          <p className="text-xs  text-white/50">
            Toggle "Use Database Processor" to keep the database version active instead of the custom processor with the same name.
          </p>
        </div>

        {loadingOverrides ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded animate-spin mx-auto mb-4"></div>
            <p className="text-white/50  text-sm">Loading processor settings...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {PROCESSOR_TYPES.map((processor) => {
              const isUsingDatabase = processorOverrides.includes(processor.key)
              return (
                <div key={processor.key} className="glass-card rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-sm font-bold text-white ">{processor.label}</h4>
                      <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-lg ${
                        isUsingDatabase
                          ? 'bg-green-500/10 text-green-400 border border-green-500/20'
                          : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                      }`}>
                        {isUsingDatabase ? 'DATABASE' : 'CUSTOM'}
                      </span>
                    </div>
                    <button
                      onClick={() => toggleProcessorOverride(processor.key)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        isUsingDatabase ? 'bg-orange-500' : 'bg-white/10'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          isUsingDatabase ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-xs text-white/35 ">{processor.description}</p>
                  <div className="mt-2 text-xs ">
                    {isUsingDatabase ? (
                      <span className="text-emerald-400">Using database configuration and rules</span>
                    ) : (
                      <span className="text-blue-700">Using custom processor from uploaded files</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default CustomProcessorsTab

